import React from 'react';
import { Button } from 'antd';

interface OfferDisplayProps {
	offerData: {
		interval: string;
		intervalCount: number;
		productName: string;
		trialDays: number;
		type: string;
		quantity: number;
		price: string;
	};
	onStartClick: () => void;
	isLoading?: boolean;
	customerName?: string;
}

const OfferDisplay: React.FC<OfferDisplayProps> = ({
	offerData,
	onStartClick,
	isLoading = false,
	customerName,
}) => {
	const formatInterval = (interval: string, count: number) => {
		if (count === 1) {
			return interval;
		}
		return `${count} ${interval}${count > 1 ? 's' : ''}`;
	};

	const formatTrialPeriod = (days: number) => {
		if (days === 0) {
			return 'No trial';
		}
		if (days === 1) {
			return '1 day trial';
		}
		return `${days} days trial`;
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-8 animate-in fade-in duration-700">
			<div className="max-w-2xl w-full">
				{/* Header with special offer badge */}
				<div className="text-center mb-8 animate-in slide-in-from-top-4 duration-1000">
					<div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold rounded-full mb-4 shadow-lg animate-pulse">
						<svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
							<path
								fillRule="evenodd"
								d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732L14.146 12.8 12.967 17.256a1 1 0 01-1.934 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732L9.854 7.2 11.033 2.744A1 1 0 0112 2z"
								clipRule="evenodd"
							/>
						</svg>
						Special Offer {customerName && `for ${customerName}`}
					</div>
					<h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
						Exclusive Deal
					</h1>
				</div>

				{/* Main offer card */}
				<div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 animate-in slide-in-from-bottom-4 duration-1000 delay-300 hover:shadow-3xl transition-all duration-300">
					{/* Gradient header */}
					<div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 px-8 py-6 text-white relative overflow-hidden">
						{/* Animated background pattern */}
						<div className="absolute inset-0 opacity-10">
							<div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16" />
							<div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12" />
						</div>
						<div className="relative flex items-center justify-between">
							<div>
								<h2 className="text-2xl font-bold mb-2">{offerData.productName}</h2>
								<p className="text-blue-100 text-lg">
									{formatInterval(offerData.interval, offerData.intervalCount)} subscription
								</p>
							</div>
							<div className="text-right">
								<div className="text-3xl font-bold">
									{offerData?.type === 'unlimited' ? 'Unlimited' : offerData.quantity + 'x'}
								</div>
								<div className="text-blue-100">Seats</div>
							</div>
						</div>

						{/* Price display */}
						<div className="mt-4 pt-4 border-t border-blue-500/30">
							<div className="flex items-center justify-center">
								<div className="text-center">
									<div className="text-sm text-blue-200 mb-1">Special Price</div>
									<div className="text-4xl font-bold">${offerData.price}</div>
									<div className="text-sm text-blue-200">
										per {formatInterval(offerData.interval, offerData.intervalCount)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Offer details */}
					<div className="p-8">
						{/* Trial period */}
						{offerData.trialDays > 0 && (
							<div className="mb-6">
								<div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200 hover:bg-green-100 transition-colors duration-200">
									<div className="flex items-center">
										<svg
											className="w-6 h-6 text-green-600 mr-3"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
										<div>
											<div className="font-semibold text-green-800">Free Trial</div>
											<div className="text-green-600">{formatTrialPeriod(offerData.trialDays)}</div>
										</div>
									</div>
									<div className="text-green-600 font-bold">
										{offerData.trialDays > 0 ? '✓' : '—'}
									</div>
								</div>
							</div>
						)}

						{/* Features list */}
						<div className="mb-8">
							<h3 className="text-lg font-semibold text-gray-900 mb-4">What's included:</h3>
							<div className="space-y-3">
								<div className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200">
									<svg
										className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700">
										{offerData?.type === 'unlimited'
											? 'Unlimited seats'
											: offerData.quantity + ' seats per ' + offerData.interval}
									</span>
								</div>
								<div className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200">
									<svg
										className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700">Full access to all features</span>
								</div>
								<div className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200">
									<svg
										className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700">Priority customer support</span>
								</div>
								<div className="flex items-center hover:bg-gray-50 p-2 rounded-lg transition-colors duration-200">
									<svg
										className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M5 13l4 4L19 7"
										/>
									</svg>
									<span className="text-gray-700">Private slack channel</span>
								</div>
							</div>
						</div>

						{/* Action button */}
						<div className="text-center">
							<Button
								type="primary"
								size="large"
								onClick={onStartClick}
								loading={isLoading}
								className="h-14 px-8 text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 border-0 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
							>
								{isLoading ? 'Processing...' : 'Start Your Journey'}
							</Button>
							{/* <p className="text-sm text-gray-500 mt-3">No commitment required • Cancel anytime</p> */}
						</div>
					</div>
				</div>

				{/* Trust indicators */}
				<div className="mt-8 text-center animate-in fade-in duration-1000 delay-700">
					<div className="flex items-center justify-center space-x-6 text-gray-400">
						<div className="flex items-center">
							<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="text-sm">Secure Payment</span>
						</div>
						<div className="flex items-center">
							<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
									clipRule="evenodd"
								/>
							</svg>
							<span className="text-sm">24/7 Support</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default OfferDisplay;
